import { generateParameterSystemName } from '@brighterion/utils/parameters';
import { Formik, FormikProps } from 'formik';
import { FormikInputField } from 'Components/FormikComponents';
import React from 'react';
import useIsLeavingForm from 'Hooks/useIsLeavingForm';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'i18next';
import { CREATE_VELOCITY_PARAM_INPUTS } from 'Common/parameterEnums';
import { HandleBlur, HandleChange } from 'Components/FormikComponents/types';
import Header from 'Routes/Parameters/Pages/CreateVelocity/Header';
import InfoOverview from 'Routes/Parameters/components/VelocityInfoOverview';
import VelocityParamStatement from 'Routes/Parameters/components/VelocityParamStatement';
import usePatchVelocityParameter from 'Hooks/query-hooks/parameters/usePatchVelocityParameter';
import Layout from 'Components/Layout';
import { PARAMETERS_PARAM_TYPES } from 'Common/enums';
import { addVelocityValidationSchema } from './EditVelocitySchema';
import styles from './EditVelocityForm.css';

type EditVelocityFormProps = {
    initialValues: ParameterVelocityFormSchema;
    disableInputField: boolean;
    velocityConfigData: VelocityConfig;
    parameterData: UseGetParametersResponse;
};

export const genParameterInfoOverviewTop = (
    t: TFunction,
    isSubmitting: boolean,
    values: ParameterVelocityFormSchema,
    handleChange: HandleChange,
    handleBlur: HandleBlur
) => {
    const generatedParameterSystemName = generateParameterSystemName(values.displayName);

    return [
        {
            id: CREATE_VELOCITY_PARAM_INPUTS.DISPLAY_NAME,
            label: t('Display Name'),
            data: (
                <FormikInputField
                    label={t('Display Name')}
                    name={CREATE_VELOCITY_PARAM_INPUTS.DISPLAY_NAME}
                    className={styles.nameField}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    disabled={isSubmitting}
                    optionalMsg={t('addValidParameterDisplayName')}
                />
            )
        },
        {
            id: CREATE_VELOCITY_PARAM_INPUTS.SYSTEM_NAME,
            label: t('System Name'),
            data: (
                <FormikInputField
                    label={t('System Name')}
                    name={CREATE_VELOCITY_PARAM_INPUTS.SYSTEM_NAME}
                    className={styles.nameField}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    // overrideValue is being used to display formatted display name in the screen
                    // it does not set the field value
                    overrideValue={generatedParameterSystemName}
                    disabled
                    optionalMsg={t('parameterSystemName')}
                />
            )
        }
    ];
};

export const genParameterInfoOverviewBottom = (
    t: TFunction,
    isSubmitting: boolean,
    handleChange: HandleChange,
    handleBlur: HandleBlur
) => [
    {
        id: CREATE_VELOCITY_PARAM_INPUTS.DESCRIPTION,
        label: t('Parameter Description'),
        data: (
            <FormikInputField
                label={t('Parameter Description')}
                name={CREATE_VELOCITY_PARAM_INPUTS.DESCRIPTION}
                className={styles.descriptionField}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disabled={isSubmitting}
            />
        )
    }
];

const EditVelocityForm: React.FC<EditVelocityFormProps> = ({
    initialValues,
    disableInputField,
    parameterData,
    velocityConfigData
}) => {
    const formRef = React.useRef<FormikProps<ParameterVelocityFormSchema>>(null);
    useIsLeavingForm(formRef);
    const { t } = useTranslation();
    const { push } = useHistory();
    const mutation = usePatchVelocityParameter(formRef);

    const handleFormSubmit = (formValues: ParameterVelocityFormSchema) => {
        const { id } = formValues;

        const changedValues: Partial<ParameterVelocityFormSchema> = {};

        // eslint-disable-next-line
        const payload: Partial<PatchVelocityParameterRequest> = {
            id,
            status: 'draft' // Hardcoded to draft until the deploy velocity feature is finished
        };

        // Check for changed properties and add to payload if they exist
        Object.entries(formValues).forEach(([key, value]) => {
            if (
                formValues[key as keyof ParameterVelocityFormSchema] !==
                initialValues[key as keyof ParameterVelocityFormSchema]
            ) {
                changedValues[key as keyof ParameterVelocityFormSchema] = value;
            }
        });

        if (changedValues.displayName) {
            payload.message = formValues.displayName!;
            payload.fieldName = generateParameterSystemName(formValues.displayName)!;
        }

        if (changedValues.description) {
            payload.description = changedValues.description;
        }

        if (changedValues.type !== undefined) {
            payload.type = changedValues.type || PARAMETERS_PARAM_TYPES.NUMBER;
        }

        if (changedValues.includeCurrentEvent !== undefined) {
            payload.includeCurrentEvent = changedValues.includeCurrentEvent;
        }

        if (changedValues.velocityType !== undefined) {
            payload.velocityType = changedValues.velocityType;
        }

        if (changedValues.timePeriod !== undefined) {
            payload.timePeriod = {
                duration: changedValues.timePeriod.duration,
                type: 'Value',
                unit: changedValues.timePeriod.unit.toString()
            };
        }

        if (changedValues.indexes !== undefined) {
            payload.indexes = changedValues.indexes;
        }

        if (changedValues.filters !== undefined) {
            payload.filters = changedValues.filters;
        }

        // Conditionally add aggregationField to the payload
        if (changedValues.aggregationField && changedValues.aggregationField.length > 0) {
            payload.aggregationField = changedValues.aggregationField.toString();
        }

        mutation.mutate(payload);
    };

    const handleCancel = () => {
        push(ROUTES.PARAMETERS.INDEX);
    };
    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
            validationSchema={addVelocityValidationSchema(t)}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { values, handleChange, handleBlur, isSubmitting } = formikProps;
                const paramInfoOverviewTopData = genParameterInfoOverviewTop(
                    t,
                    isSubmitting,
                    values,
                    handleChange,
                    handleBlur
                );
                const paramInfoOverviewBottomData = genParameterInfoOverviewBottom(
                    t,
                    isSubmitting,
                    handleChange,
                    handleBlur
                );

                return (
                    <Layout>
                        <Header handleCancel={handleCancel} mode='edit' />
                        <InfoOverview
                            dataTestId='velocityParameterInformation'
                            leftTitle={t('Parameter Information')}
                            topData={paramInfoOverviewTopData}
                            bottomData={paramInfoOverviewBottomData}
                            isSubmitting={isSubmitting}
                            values={values}
                        />
                        <VelocityParamStatement
                            disableInputField={disableInputField}
                            parameterData={parameterData}
                            velocityConfigData={velocityConfigData}
                        />
                    </Layout>
                );
            }}
        </Formik>
    );
};

export default EditVelocityForm;
